let logger;
let counter = 0;

const AudioPlaybackProvider = {
  /**
   *
   * @param {string} harnessName
   * @param {object} playerMethods
   * @param {object} eventBinders
   * @param {object} eventRemovers
   * @returns {AudioProvider}
   */
  createAudioProvider(harnessName, playerMethods, eventBinders, eventRemovers) {
    const providerId = counter++;

    /**
     * @typedef {object} AudioProvider
     * @property
     */
    return {
      _id: providerId,

      play(startVolume, gain, isMuted, startTime) {
        return AudioPlaybackProvider.play(playerMethods, startVolume, gain, isMuted, startTime);
      },

      pause() {
        return AudioPlaybackProvider.pause(playerMethods);
      },

      setCurrentTime(time) {
        return AudioPlaybackProvider.setCurrentTime(playerMethods, time);
      },

      setVolume(volume, gain) {
        return AudioPlaybackProvider.setVolume(playerMethods, volume, gain);
      },

      setMuted(isMuted) {
        return AudioPlaybackProvider.setMuted(playerMethods, isMuted);
      },

      updateUrl(audioUrl) {
        return AudioPlaybackProvider.updateUrl(playerMethods, audioUrl);
      },

      bindEvent: (eventName, handlerFn) => {
        return AudioPlaybackProvider.bindPlaybackEventToPlayer(
          eventBinders,
          playerMethods.getState,
          eventName,
          handlerFn,
        );
      },

      bindEvents: eventBindings => {
        return Object.keys(eventBindings).forEach(eventName => {
          AudioPlaybackProvider.bindPlaybackEventToPlayer(
            eventBinders,
            playerMethods.getState,
            eventName,
            eventBindings[eventName],
          );
        });
      },

      unbindEvent: eventName => {
        return AudioPlaybackProvider.unbindPlaybackEventFromPlayer(eventRemovers, eventName);
      },

      unbindEvents: eventNames => {
        return eventNames.forEach(eventName =>
          AudioPlaybackProvider.unbindPlaybackEventFromPlayer(eventRemovers, eventName),
        );
      },

      _destroy() {
        return AudioPlaybackProvider.destroy(playerMethods);
      },

      getState() {
        return AudioPlaybackProvider.getState(playerMethods);
      },

      getHarnessName() {
        return harnessName;
      },
    };
  },

  /**
   * Sets the local logger variable.
   *
   * @param {object} _logger_
   */
  setLogger(_logger_) {
    logger = _logger_;
  },

  /**
   * Logs errors using the logger.
   *
   * @param {string} msg
   * @returns {*}
   */
  error(msg) {
    if (logger) {
      return logger.error(msg);
    }
  },

  /**
   * Normalizes the volume based on a "gain" value
   *
   * @param {number} volume
   * @param {number} gain
   * @returns {number}
   * @private
   */
  _calculateRealVolume(volume, gain) {
    if (!Number.isFinite(gain)) {
      AudioPlaybackProvider.error('"gain" must be a number');
      return volume;
    }

    // Get the skinny: https://wiki.savagebeast.com/display/eng/Volume+Normalization+-+Client-side
    return Math.round(Math.pow(10, gain / 20) * volume);
  },

  /**
   * Sets the volume on the provider for the given `audioSrc`
   *
   * @param {object} playerMethods
   * @param {number} volume - 1 ... 100
   * @param {number} gain
   * @returns {Promise}
   */
  setVolume(playerMethods, volume, gain) {
    let vol = volume;

    if (!Number.isFinite(vol)) {
      return AudioPlaybackProvider.error('"volume" must be a number');
    }

    if (typeof gain !== 'undefined') {
      vol = AudioPlaybackProvider._calculateRealVolume(vol, gain);
    }

    if (vol > 100) {
      vol = 100;
    } else if (vol < 0) {
      vol = 0;
    } else {
      vol = Math.floor(vol);
    }
    return playerMethods.setVolume(vol);
  },

  /**
   * Set the muted value on the provider for the given `audioSrc`
   *
   * @param {object} playerMethods
   * @param {bool} isMuted
   * @returns {Promise}
   */
  setMuted(playerMethods, isMuted) {
    return playerMethods.setMuted(isMuted);
  },

  updateUrl(playerMethods, audioUrl) {
    if (!playerMethods.updateUrl) {
      return AudioPlaybackProvider.error('trying to update URL for a non-supported provider');
    }
    return playerMethods.updateUrl(audioUrl);
  },

  /**
   * Set the current time on the provider for the given `audioSrc`
   *
   * @param {object} playerMethods
   * @param {number} currentTime
   * @param {bool} fromPlayClick
   * @returns {Promise}
   */
  setCurrentTime(playerMethods, currentTime, fromPlayClick) {
    if (!Number.isFinite(currentTime)) {
      return AudioPlaybackProvider.error('"currentTime" must be a number');
    }

    return playerMethods.setCurrentTime(currentTime < 0 ? 0 : currentTime, fromPlayClick);
  },

  /**
   * Plays audio on the provider for the given `audioSrc`
   *
   * @param {object} playerMethods
   * @param {number} startVolume
   * @param {number} gain
   * @param {number} isMuted
   * @param {bool} startTime
   * @returns {Promise}
   */
  play(playerMethods, startVolume, gain, isMuted, startTime) {
    if (typeof startVolume !== 'undefined') {
      AudioPlaybackProvider.setVolume(playerMethods, startVolume, gain);
    }

    if (typeof isMuted !== 'undefined') {
      AudioPlaybackProvider.setMuted(playerMethods, isMuted);
    }

    if (typeof startTime !== 'undefined') {
      AudioPlaybackProvider.setCurrentTime(playerMethods, startTime, true);
    }
    return playerMethods.play(startTime);
  },

  /**
   * Pauses audio on the provider for the given `audioSrc`
   *
   * @param {object} playerMethods
   * @returns {Promise}
   */
  pause(playerMethods) {
    return playerMethods.pause();
  },

  destroy(playerMethods) {
    return playerMethods.destroy();
  },

  getState(playerMethods) {
    return playerMethods.getState();
  },

  /**
   *
   * @param {object} eventBinders
   * @param {Function} getState
   * @param {string} eventName
   * @param {Function} eventHandler
   * @returns {Promise}
   */
  bindPlaybackEventToPlayer(eventBinders, getState, eventName, eventHandler) {
    // Wraps the event handler with our custom logic that injects the provider & playerState as arguments
    function eventHandlerWrapper(err) {
      let error;

      if (err instanceof Error) {
        error = err;
      }

      eventHandler(getState(), error);
    }

    return eventBinders[eventName](eventHandlerWrapper);
  },

  /**
   *
   * @param {object} eventRemovers
   * @param {string} eventName
   * @returns {Promise}
   */
  unbindPlaybackEventFromPlayer(eventRemovers, eventName) {
    return eventRemovers[eventName]();
  },
};

export default AudioPlaybackProvider;
